import { Box, Flex, Separator, Text } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { CommonCheckboxInput } from 'components/common/form/checkbox';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { USER_ROLES, UserRole } from 'lib_ts/enums/auth.enums';
import {
  FeatureFlagCriterionType,
  FeatureFlagMachineMode,
} from 'lib_ts/enums/feature-flags.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IFeatureFlagCriterion } from 'lib_ts/interfaces/feature-flags/i-feature-flag';
import { useEffect, useMemo, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import { AdminTeamsService } from 'services/admin/teams.service';
import { AdminUsersService } from 'services/admin/users.service';

const MACHINE_MODE_OPTIONS: IOption[] = [
  { label: 'All machines', value: FeatureFlagMachineMode.All },
  { label: 'Specific machines', value: FeatureFlagMachineMode.List },
];

/** pulls the criterion of a given type out of a (potentially mixed) list */
const findCriterion = <T extends FeatureFlagCriterionType>(
  criteria: IFeatureFlagCriterion[],
  type: T
): Extract<IFeatureFlagCriterion, { type: T }> | undefined =>
  criteria.find((c) => c.type === type) as
    | Extract<IFeatureFlagCriterion, { type: T }>
    | undefined;

/** what the composer reports up to the dialog on every change */
export interface IFeatureFlagCriteriaState {
  /** the composed, non-empty criteria ready to persist */
  criteria: IFeatureFlagCriterion[];
  /**
   * human-readable names of dimensions that are toggled on but have no
   * selection yet (so the dialog can block save with a standard message).
   */
  incomplete: string[];
}

interface IProps {
  /** criteria to prefill the composer from (edit) */
  initialCriteria: IFeatureFlagCriterion[];
  disabled?: boolean;
  /** fires whenever the composed criteria (or their validity) change */
  onChange: (state: IFeatureFlagCriteriaState) => void;
}

/**
 * Targeting composer for a single flag. Each dimension (Role / User / Team /
 * Machine) is an independent, optional criterion; when enabled its selections
 * are OR-composed, and the dimensions are AND-composed by the shared evaluator.
 * A dimension that is toggled on but left empty is dropped (and flagged inline)
 * so it never silently narrows targeting.
 */
export const FeatureFlagCriteriaComposer = (props: IProps) => {
  const initialRole = findCriterion(
    props.initialCriteria,
    FeatureFlagCriterionType.Role
  );
  const initialUser = findCriterion(
    props.initialCriteria,
    FeatureFlagCriterionType.User
  );
  const initialTeam = findCriterion(
    props.initialCriteria,
    FeatureFlagCriterionType.Team
  );
  const initialMachine = findCriterion(
    props.initialCriteria,
    FeatureFlagCriterionType.Machine
  );

  const [roleOn, setRoleOn] = useState(!!initialRole);
  const [roleValues, setRoleValues] = useState<string[]>(
    initialRole?.roles ?? []
  );

  const [userOn, setUserOn] = useState(!!initialUser);
  const [userValues, setUserValues] = useState<string[]>(
    initialUser?.userIds ?? []
  );

  const [teamOn, setTeamOn] = useState(!!initialTeam);
  const [teamValues, setTeamValues] = useState<string[]>(
    initialTeam?.teamIds ?? []
  );

  const [machineOn, setMachineOn] = useState(!!initialMachine);
  const [machineMode, setMachineMode] = useState<FeatureFlagMachineMode>(
    initialMachine?.mode ?? FeatureFlagMachineMode.All
  );
  const [machineValues, setMachineValues] = useState<string[]>(
    initialMachine?.machineIds ?? []
  );

  // option sources (Platform Console lacks the admin providers, so we fetch the
  // global lists directly; staff are super admins so `global` is permitted)
  const [teamOptions, setTeamOptions] = useState<IOption[]>([]);
  const [userOptions, setUserOptions] = useState<IOption[]>([]);
  const [machineOptions, setMachineOptions] = useState<IOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setOptionsLoading(true);

      try {
        const [teams, users, machines] = await Promise.all([
          AdminTeamsService.getInstance().getTeams(true),
          AdminUsersService.getInstance().getUsers(true),
          AdminMachinesService.getInstance().getMachines(true),
        ]);

        if (!active) {
          return;
        }

        setTeamOptions(
          teams
            .map((tm) => ({
              label: tm.name,
              group: tm.league,
              value: tm._id,
            }))
            .sort((a, b) => StringHelper.safeSort(a.label, b.label))
        );

        setUserOptions(
          users
            .map((u) => ({
              label: u.email,
              group: u.email.split('@')[1]?.toUpperCase(),
              value: u._id,
            }))
            .sort((a, b) => StringHelper.safeSort(a.label, b.label))
        );

        setMachineOptions(
          machines
            .filter((m) => !m.deleted)
            .map((m) => ({
              label: MachineContextHelper.getMachineMenuLabel(m),
              value: m.machineID,
            }))
            .sort((a, b) => StringHelper.safeSort(a.label, b.label))
        );
      } catch (e) {
        console.error('Failed to load feature-flag criteria options', e);
      } finally {
        if (active) {
          setOptionsLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, []);

  const state = useMemo<IFeatureFlagCriteriaState>(() => {
    const criteria: IFeatureFlagCriterion[] = [];
    const incomplete: string[] = [];

    if (roleOn) {
      if (roleValues.length > 0) {
        criteria.push({
          type: FeatureFlagCriterionType.Role,
          roles: roleValues as UserRole[],
        });
      } else {
        incomplete.push('role');
      }
    }

    if (userOn) {
      if (userValues.length > 0) {
        criteria.push({
          type: FeatureFlagCriterionType.User,
          userIds: userValues,
        });
      } else {
        incomplete.push('user');
      }
    }

    if (teamOn) {
      if (teamValues.length > 0) {
        criteria.push({
          type: FeatureFlagCriterionType.Team,
          teamIds: teamValues,
        });
      } else {
        incomplete.push('team');
      }
    }

    if (machineOn) {
      if (machineMode === FeatureFlagMachineMode.All) {
        criteria.push({
          type: FeatureFlagCriterionType.Machine,
          mode: FeatureFlagMachineMode.All,
        });
      } else if (machineValues.length > 0) {
        criteria.push({
          type: FeatureFlagCriterionType.Machine,
          mode: FeatureFlagMachineMode.List,
          machineIds: machineValues,
        });
      } else {
        incomplete.push('machine');
      }
    }

    return { criteria, incomplete };
  }, [
    roleOn,
    roleValues,
    userOn,
    userValues,
    teamOn,
    teamValues,
    machineOn,
    machineMode,
    machineValues,
  ]);

  const { onChange } = props;

  useEffect(() => {
    onChange(state);
  }, [state, onChange]);

  const emptyHint = (
    <Text size="1" color={RADIX.COLOR.WARNING}>
      Select at least one value, or turn this criterion off.
    </Text>
  );

  return (
    <Box>
      <Separator size="4" mb="3" />

      <Flex direction="column" gap="1" mb="3">
        <Text weight="medium">Targeting criteria</Text>
        <Text size="1" color="gray">
          Leave all off for a simple on/off toggle. Enabled criteria are
          combined with AND; within a criterion, any match qualifies.
        </Text>
      </Flex>

      <Flex direction="column" gap="3">
        <Box>
          <CommonCheckboxInput
            id="feature-flag-criterion-role"
            label="Role"
            name="criterion-role"
            disabled={props.disabled}
            checked={roleOn}
            onChange={(v) => setRoleOn(!!v)}
          />
          {roleOn && (
            <Box mt="2">
              <CommonSearchInput
                id="feature-flag-role-values"
                name="role-values"
                placeholder="Select roles"
                multiple
                disabled={props.disabled}
                options={USER_ROLES}
                values={roleValues}
                onChange={setRoleValues}
                maxDisplayItems={3}
                optional
              />
              {roleValues.length === 0 && emptyHint}
            </Box>
          )}
        </Box>

        <Box>
          <CommonCheckboxInput
            id="feature-flag-criterion-user"
            label="User"
            name="criterion-user"
            disabled={props.disabled}
            checked={userOn}
            onChange={(v) => setUserOn(!!v)}
          />
          {userOn && (
            <Box mt="2">
              <CommonSearchInput
                id="feature-flag-user-values"
                name="user-values"
                placeholder={optionsLoading ? 'Loading users…' : 'Select users'}
                multiple
                disabled={props.disabled || optionsLoading}
                options={userOptions}
                values={userValues}
                onChange={setUserValues}
                maxDisplayItems={2}
                optional
              />
              {userValues.length === 0 && emptyHint}
            </Box>
          )}
        </Box>

        <Box>
          <CommonCheckboxInput
            id="feature-flag-criterion-team"
            label="Team"
            name="criterion-team"
            disabled={props.disabled}
            checked={teamOn}
            onChange={(v) => setTeamOn(!!v)}
          />
          {teamOn && (
            <Box mt="2">
              <CommonSearchInput
                id="feature-flag-team-values"
                name="team-values"
                placeholder={optionsLoading ? 'Loading teams…' : 'Select teams'}
                multiple
                disabled={props.disabled || optionsLoading}
                options={teamOptions}
                values={teamValues}
                onChange={setTeamValues}
                maxDisplayItems={2}
                optional
              />
              {teamValues.length === 0 && emptyHint}
            </Box>
          )}
        </Box>

        <Box>
          <CommonCheckboxInput
            id="feature-flag-criterion-machine"
            label="Machine"
            name="criterion-machine"
            disabled={props.disabled}
            checked={machineOn}
            onChange={(v) => setMachineOn(!!v)}
          />
          {machineOn && (
            <Flex direction="column" gap="2" mt="2">
              <CommonSelectInput
                id="feature-flag-machine-mode"
                name="machine-mode"
                disabled={props.disabled}
                options={MACHINE_MODE_OPTIONS}
                value={machineMode}
                onChange={(v) => setMachineMode(v as FeatureFlagMachineMode)}
              />
              {machineMode === FeatureFlagMachineMode.List && (
                <Box>
                  <CommonSearchInput
                    id="feature-flag-machine-values"
                    name="machine-values"
                    placeholder={
                      optionsLoading ? 'Loading machines…' : 'Select machines'
                    }
                    multiple
                    disabled={props.disabled || optionsLoading}
                    options={machineOptions}
                    values={machineValues}
                    onChange={setMachineValues}
                    maxDisplayItems={2}
                    optional
                  />
                  {machineValues.length === 0 && emptyHint}
                </Box>
              )}
              {machineMode === FeatureFlagMachineMode.All && (
                <Text size="1" color="gray">
                  Matches any request that carries a machine (fails closed for
                  contexts without one).
                </Text>
              )}
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
